import React from "react";
import { useStore, STORE_OPTIONS } from "../context/StoreContext";
import { useLocale } from "../context/LocaleContext";
import { EVENT_LOCALE_SET, events } from "../embed/events";

const SPA_BACKEND_KEY = "ww/spa/backendMode" as const;
type BackendMode = "dev" | "prod";

const SPA_LANGUAGE_KEY = "ww/spa/language" as const;
const SPA_MARKET_KEY = "ww/spa/market" as const;

function readBackendMode(): BackendMode {
  try {
    const saved = localStorage.getItem(SPA_BACKEND_KEY);
    if (saved === "dev" || saved === "prod") return saved;
  } catch {}
  return "prod";
}

export function SettingsDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { storeId, setStoreId } = useStore();
  const { language, market } = useLocale();
  const [backendMode, setBackendMode] = React.useState<BackendMode>(() =>
    typeof window === "undefined" ? "prod" : readBackendMode()
  );

  // When opening, sync UI with persisted value (in case it changed elsewhere).
  React.useEffect(() => {
    if (!open) return;
    setBackendMode(readBackendMode());
  }, [open]);

  // Escape closes the drawer.
  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () =>
      window.removeEventListener("keydown", onKeyDown, { capture: true } as any);
  }, [open, onClose]);

  const applyBackendMode = React.useCallback((next: BackendMode) => {
    try {
      localStorage.setItem(SPA_BACKEND_KEY, next);
    } catch {}
    setBackendMode(next);
    // Matches current SPA behavior: backend mode affects init/token/chat origins.
    window.location.reload();
  }, []);

  const applyLocale = React.useCallback(
    (next: { language: string; market: string }) => {
      try {
        localStorage.setItem(SPA_LANGUAGE_KEY, next.language);
        localStorage.setItem(SPA_MARKET_KEY, next.market);
      } catch {}
      // Update the app immediately via the existing locale event.
      events.emit(EVENT_LOCALE_SET, next);
    },
    []
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60]">
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 bg-black/20"
        aria-label="Close settings"
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
        className="absolute top-0 right-0 h-full w-[340px] max-w-[90vw] bg-white shadow-xl border-l transform-gpu transition-transform duration-200 ease-out translate-x-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="text-sm font-semibold">Settings</div>
            <button
              type="button"
              className="px-2 py-1 text-xs rounded border"
              onClick={onClose}
              aria-label="Close settings"
            >
              Close
            </button>
          </div>

          <div className="p-4 space-y-4">
            <div className="space-y-2">
              <div className="text-xs font-medium text-gray-700">Backend</div>
              <select
                value={backendMode}
                onChange={(e) => applyBackendMode(e.target.value as BackendMode)}
                className="w-full px-2 py-2 text-sm rounded border bg-white"
                aria-label="Backend mode"
              >
                <option value="prod">Prod</option>
                <option value="dev">Dev</option>
              </select>
              <div className="text-[11px] text-gray-500">
                Changing backend reloads the SPA.
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-xs font-medium text-gray-700">Store</div>
              <select
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
                className="w-full px-2 py-2 text-sm rounded border bg-white"
                aria-label="Store"
              >
                {STORE_OPTIONS.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <div className="text-xs font-medium text-gray-700">Language (SPA)</div>
              <select
                value={language}
                onChange={(e) =>
                  applyLocale({ language: e.target.value, market })
                }
                className="w-full px-2 py-2 text-sm rounded border bg-white"
                aria-label="Language"
              >
                <option value="sv">sv</option>
                <option value="en">en</option>
                <option value="da">da</option>
                <option value="nb">nb</option>
              </select>
            </div>

            <div className="space-y-2">
              <div className="text-xs font-medium text-gray-700">Market (SPA)</div>
              <select
                value={market}
                onChange={(e) =>
                  applyLocale({ language, market: e.target.value })
                }
                className="w-full px-2 py-2 text-sm rounded border bg-white"
                aria-label="Market"
              >
                <option value="se">se</option>
                <option value="no">no</option>
                <option value="dk">dk</option>
                <option value="fi">fi</option>
                <option value="de">de</option>
                <option value="fr">fr</option>
                <option value="nl">nl</option>
                <option value="us">us</option>
                <option value="gb">gb</option>
              </select>
            </div>
          </div>

          <div className="mt-auto p-4 border-t">
            <div className="text-[11px] text-gray-500">
              SPA-only settings (does not ship inside the widget embed).
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


